/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global console, document, Excel, Office */
import { buildBatchFunction, buildFetchBatchResult, type FunctionPayload, sysExit } from "./functions";

const RUN_BUTTON_LABEL = "実行";
const STOP_BUTTON_LABEL = "キャンセル";
const STOP_STATE = "stop";
const RUN_STATE = "run";

import * as process from "process";

import { getEmail, getMeta } from "./utils";
import { type GetGLPGSFuncsType, GLGPSFuncs } from "./utils/funcs";
import { type CellPosition, SecureProfileManager } from "./utils/profile";
import { StateManager } from "./utils/state";
import { AdytonEdge } from "./api/adyton-edge";
import { AdytonEdgeStub } from "./api/stub/adyton-edge";

Office.onReady((info) => {
  if (info.host === Office.HostType.Excel) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "flex";
    document.getElementById("run").onclick = run;
  }
});

export async function run() {
  const password = (document.getElementById("passwordInput") as HTMLInputElement).value;
  const runButton = document.getElementById("runButton") as HTMLSpanElement;
  const spinner = document.getElementsByClassName("runningSpinner")[0] as HTMLDivElement;
  const errorMessageArea = document.getElementsByClassName("errorMessageArea")[0] as HTMLDivElement;
  const sm = new StateManager();
  const spm = new SecureProfileManager();

  sm.saveState(RUN_STATE);

  const pollingMs = Number(process.env["POLLING_MS"]);

  let execPlace: { row: number; column: number };
  let errorMessage: string | undefined = undefined;

  try {
    if (password === "") {
      throw new Error("パスワードを入力してください");
    }

    await Excel.run(async (context) => {
      async function poll<Result>(
        fetch: () => Promise<Result | undefined>,
        maxRemainsMx: number
      ): Promise<Result | undefined> {
        return await new Promise<Result>((resolve, reject) => {
          async function fetcher(remainMs: number): Promise<void> {
            const state = await sm.getState();
            if (state === STOP_STATE) {
              resolve(undefined);
              return;
            }

            const result = await fetch();
            if (result !== undefined) {
              resolve(result);
              return;
            }

            if (remainMs <= 0) {
              reject(
                "実行時間が経過しました。再度実行しても同じエラーが発生する場合は、システム担当者にお問い合わせお願いします。"
              );
              return;
            }

            // セッション保持のためにcontext.sync()を実行
            await context.sync();
            setTimeout(() => fetcher(remainMs - pollingMs), remainMs < pollingMs ? remainMs : pollingMs);
          }

          fetcher(maxRemainsMx);
        });
      }

      const range = context.workbook.getSelectedRange();
      let sheet = context.workbook.worksheets.getActiveWorksheet();

      sheet.load("name");
      await context.sync();
      const profileKey = sheet.name;

      if (runButton.innerText === STOP_BUTTON_LABEL) {
        sm.saveState(STOP_STATE);
        return;
      }

      __init();
      const email = await getEmail(sheet, context);

      const meta = getMeta();

      const adytonEdge: AdytonEdge | AdytonEdgeStub =
        process.env["USED_STUB"] === "true"
          ? new AdytonEdgeStub()
          : new AdytonEdge(process.env["ADYTON_EDGE_DOMAIN"], email, password, meta);

      const batchFunction = buildBatchFunction(adytonEdge);
      const fetchBatchResult = buildFetchBatchResult(poll, adytonEdge);

      async function writeResult(values: string[], { row, column }: CellPosition): Promise<void> {
        const resultRange = sheet.getRangeByIndexes(
          row, column, 1, values.length
        );
        resultRange.load("values");
        await context.sync();

        const isAllEmpty = resultRange.values.flat().every((v) => v === "");
        console.log(values, resultRange.values, isAllEmpty, values.length)
        // 書き込み先の範囲に値のあるセルがあればエラーにするが、
        // 1番目のセルに値がある場合はその値を上書きしてしまわないように敢えてエラーを書き込まない
        // （処理の途中で後から値が書き込まれたときに発生する条件の想定）
        if (!isAllEmpty && resultRange.values[0][0] === "") {
          sheet.getCell(row, column).values = [["エラー: 書き込み先に値が入っているセルがあるため結果を書き込めませんでした"]];
          await context.sync();
        } else if (isAllEmpty) {
          resultRange.values = [values];
          await context.sync();
        }
      }

      async function resumePrevTask(): Promise<void> {
        try {
          const profile = await spm.getProfile(profileKey, password);
          if (!profile) {
            return;
          }

          const result = await fetchBatchResult(profile.paths, profile.timeout);
          if (result.values !== undefined) {
            await writeResult(result.values, profile.cellPosition);
          }
        } finally {
          await spm.deleteProfile(profileKey);
        }
      }

      async function executeFunction(func: GetGLPGSFuncsType): Promise<FunctionPayload> {
        const { function: name, params } = await glpgsFuncs.formatter(func.value);
        switch (name) {
          case "sys_exit":
            return await sysExit(func, params);
          default:
            return await batchFunction(func, name, params);
        }
      }

      await resumePrevTask();

      const glpgsFuncs = new GLGPSFuncs(context, sheet, process.env["GLPGS_FUNCS_NAME"]);
      const funcs = await glpgsFuncs.get();
      console.log("GLPGS Functions : ", funcs);

      for (const func of funcs) {
        execPlace = { row: func.row, column: func.column };

        const payload = await executeFunction(func);
        switch (payload.type) {
          case "sync":
            await writeResult(payload.result.values, payload.cellPosition);
            if (payload.result.abort) {
              return;
            }
            break;
          case "batch":
            await spm.saveProfile(
              {
                cellPosition: payload.cellPosition,
                paths: payload.paths,
                password,
                timeout: payload.timeout,
              },
              profileKey,
              password
            );
            const result = await fetchBatchResult(payload.paths, payload.timeout);
            if (result.values !== undefined) {
              await writeResult(result.values, payload.cellPosition);
            }
            await spm.deleteProfile(profileKey);
            if (result.abort) {
              return;
            }
            break;
        }
      }
    });
  } catch (error) {
    errorMessage = execPlace ? `${error}\nError場所 : ${execPlace.row + 1}行目${execPlace.column + 1}列目` : error;
  } finally {
    sm.deleteState()
    await __end(errorMessage);
  }

  function __init() {
    runButton.innerText = STOP_BUTTON_LABEL;
    spinner.style.display = "block";
    errorMessageArea.style.display = "none";
  }

  async function __end(errorMessage?: string) {
    runButton.innerText = RUN_BUTTON_LABEL;
    spinner.style.display = "none";
    if (errorMessage) {
      errorMessageArea.style.display = "block";
      errorMessageArea.innerText = errorMessage;
    }
  }
}
